import React from "react";
import { useLanguage } from "../../i18n/LanguageContext";
import { Label } from "../ui/label";

const PRESETS = ["12m", "3y", "5y", "all", "custom"];

export function recordIsAi(item) {
  return (
    item?.kind === "case"
    || item?.category === "ai_second_opinion"
    || item?.source === "medconcil_case"
  );
}

export function eligibleRecords(records, includeAi) {
  return (records || []).filter((item) => includeAi || !recordIsAi(item));
}

export function utcTodayIso() {
  return new Date().toISOString().slice(0, 10);
}

export function shiftCalendarYears(isoDay, years) {
  const [year, month, day] = String(isoDay || "").split("-").map(Number);
  if (!year || !month || !day) return isoDay;
  const targetYear = year - years;
  const last = new Date(Date.UTC(targetYear, month, 0)).getUTCDate();
  const clamped = Math.min(day, last);
  return `${targetYear}-${String(month).padStart(2, "0")}-${String(clamped).padStart(2, "0")}`;
}

export function periodWindow(scope, today) {
  const preset = scope?.periodPreset || "3y";
  const end = today || utcTodayIso();
  if (preset === "all") return null;
  if (preset === "12m") return { start: shiftCalendarYears(end, 1), end };
  if (preset === "3y") return { start: shiftCalendarYears(end, 3), end };
  if (preset === "5y") return { start: shiftCalendarYears(end, 5), end };
  const from = String(scope?.periodFrom || "").trim();
  let to = String(scope?.periodTo || "").trim() || end;
  if (to > end) to = end;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) return { start: "9999-12-31", end: "1900-01-01" };
  return { start: from, end: to };
}

function careOverlaps(eventDate, precision, start, end) {
  const text = String(eventDate || "").trim();
  if (precision === "day") return text >= start && text <= end;
  if (precision === "month" && /^\d{4}-\d{2}$/.test(text)) {
    const [year, month] = text.split("-").map(Number);
    const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const monthStart = `${text}-01`;
    const monthEnd = `${text}-${String(last).padStart(2, "0")}`;
    return monthStart <= end && monthEnd >= start;
  }
  if (precision === "year" && /^\d{4}$/.test(text)) {
    return `${text}-01-01` <= end && `${text}-12-31` >= start;
  }
  return false;
}

export function recordInPeriod(record, scope, today) {
  const window = periodWindow(scope, today);
  if (!window) return true;
  const dated = record?.event_date && record?.event_date_precision;
  if (!dated) return scope?.includeUndated !== false;
  return careOverlaps(record.event_date, record.event_date_precision, window.start, window.end);
}

export function customPeriodError(scope) {
  if ((scope?.periodPreset || "3y") !== "custom") return "";
  const from = String(scope?.periodFrom || "").trim();
  const to = String(scope?.periodTo || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || from < "1900-01-01") return "invalid";
  if (to && (!/^\d{4}-\d{2}-\d{2}$/.test(to) || from > to)) return "invalid";
  return "";
}

const DATE_LOCALE = { en: "en-GB", ru: "ru-RU", es: "es-ES", fr: "fr-FR", ar: "ar" };

export function formatCareDate(iso, language) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  if (!match) return String(iso || "");
  const parsed = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  const locale = DATE_LOCALE[language] || DATE_LOCALE.en;
  try {
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(parsed);
  } catch {
    return String(iso || "");
  }
}

export function periodRangeLabel(period, t, language) {
  const preset = period?.preset || period?.period_label || "whole";
  const key = preset === "all" ? "whole" : preset;
  const label = t(`history.shareScopePeriod.${key}`);
  const from = period?.period_from;
  const to = period?.period_to;
  if (!from || !to || key === "whole") return label;
  return t("history.shareScopePeriodRange")
    .replace("{label}", label)
    .replace("{from}", formatCareDate(from, language))
    .replace("{to}", formatCareDate(to, language));
}

export function scopePayload(scope) {
  const note = (scope.note || "").trim();
  const preset = scope.periodPreset || "3y";
  let period;
  if (preset === "all") {
    period = { preset: "all", include_undated: true };
  } else if (preset === "custom") {
    const to = String(scope.periodTo || "").trim();
    period = {
      preset: "custom",
      from: String(scope.periodFrom || "").trim(),
      to: to || null,
      include_undated: scope.includeUndated !== false,
    };
  } else {
    period = {
      preset,
      include_undated: scope.includeUndated !== false,
    };
  }
  const excluded = scope.excludedIds || [];
  return {
    exclude_record_ids: excluded.length ? excluded : null,
    include_profile: Boolean(scope.includeProfile),
    include_identity: Boolean(scope.includeIdentity),
    include_ai_opinions: Boolean(scope.includeAi),
    attach_documents: Boolean(scope.attachDocuments),
    note_for_doctor: note || null,
    period,
  };
}

export function previewScopePayload(scope) {
  const payload = scopePayload(scope);
  delete payload.note_for_doctor;
  return payload;
}

function isCaseAttachment(item) {
  return Boolean(item && item.source === "case_attachment" && item.query_id);
}

function byUploadTime(left, right) {
  const a = String(left?.created_at || "");
  const b = String(right?.created_at || "");
  if (a < b) return -1;
  if (a > b) return 1;
  return String(left?.record_id || "").localeCompare(String(right?.record_id || ""));
}

export function pickerRows(records) {
  const list = records || [];
  const filesByQuery = new Map();
  const caseByQuery = new Map();
  for (const item of list) {
    if (item?.kind === "case" && item.query_id && !caseByQuery.has(item.query_id)) {
      caseByQuery.set(item.query_id, item);
    }
    if (isCaseAttachment(item)) {
      const bucket = filesByQuery.get(item.query_id) || [];
      bucket.push(item);
      filesByQuery.set(item.query_id, bucket);
    }
  }
  for (const bucket of filesByQuery.values()) bucket.sort(byUploadTime);

  const emitted = new Set();
  const rows = [];
  for (const item of list) {
    const queryId = item?.query_id;
    const files = queryId ? filesByQuery.get(queryId) : null;
    const grouped = Boolean(
      files && files.length && (isCaseAttachment(item) || item?.kind === "case"),
    );
    if (!grouped) {
      rows.push({ type: "record", item });
      continue;
    }
    if (emitted.has(queryId)) continue;
    const caseRecord = caseByQuery.get(queryId) || null;
    if (caseRecord && item.record_id !== caseRecord.record_id) continue;
    emitted.add(queryId);
    rows.push({ type: "group", queryId, caseRecord, files });
  }
  return rows;
}

function groupHeaderText(caseRecord, files, t) {
  const link = (files || []).map((file) => file.case).find(Boolean) || {};
  const date = (caseRecord && caseRecord.event_date) || link.event_date || "";
  const title = (caseRecord && caseRecord.title) || link.title || "";
  const dateText = date || t("history.dateUnknown");
  return title ? `${dateText} · ${title}` : dateText;
}

function Check({ testId, checked, onChange, label }) {
  return (
    <label className="flex items-start gap-2 text-sm">
      <input
        type="checkbox"
        className="mt-1"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        data-testid={testId}
      />
      <span>{label}</span>
    </label>
  );
}

function GroupCheckbox({ testId, checked, indeterminate, onChange, label }) {
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        data-testid={testId}
      />
      <span className="truncate">{label}</span>
    </label>
  );
}

function RecordChoice({ item, excluded, onToggle, t, indented }) {
  const checked = !excluded.includes(item.record_id);
  return (
    <label className={`flex items-center gap-2 text-sm ${indented ? "pl-6" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onToggle(item.record_id, event.target.checked)}
        data-testid={`scope-record-${item.record_id}`}
      />
      <span className="truncate">
        {item.event_date || t("history.dateUnknown")}
        {" · "}
        {item.title || t(`history.category.${item.category}`)}
      </span>
    </label>
  );
}

export default function ScopePicker({
  records,
  scope,
  onChange,
  previewLine,
  periods,
  today,
  recordsTotal,
}) {
  const { t } = useLanguage();
  const set = (patch) => onChange({ ...scope, ...patch });
  const clock = today || periods?.today || utcTodayIso();
  const visible = eligibleRecords(records, scope.includeAi)
    .filter((item) => recordInPeriod(item, scope, clock));
  const excluded = scope.excludedIds || [];
  const preset = scope.periodPreset || "3y";
  const undatedCount = Number(periods?.undated) || 0;
  const loadedCount = (records || []).length;
  const showTruncation = Number(recordsTotal) > loadedCount;

  const toggleRecord = (recordId, checked) => {
    const next = checked
      ? excluded.filter((id) => id !== recordId)
      : [...excluded, recordId].filter((id, index, all) => all.indexOf(id) === index);
    set({ excludedIds: next, choosing: true });
  };

  const toggleGroup = (ids, checked) => {
    const idSet = new Set(ids);
    const next = checked
      ? excluded.filter((id) => !idSet.has(id))
      : [...excluded, ...ids.filter((id) => !excluded.includes(id))];
    set({ excludedIds: next, choosing: true });
  };

  const selectPreset = (id) => set({ periodPreset: id, excludedIds: [] });

  return (
    <div className="space-y-3" data-testid="scope-picker">
      <p className="text-sm font-medium">{t("history.packetStepInclude")}</p>
      <div className="flex flex-wrap gap-2" data-testid="scope-period" role="group">
        {PRESETS.map((id) => {
          const label = t(`history.period.${id}`);
          const count = id === "custom" ? null : periods?.counts?.[id];
          const text = count == null
            ? label
            : t("history.periodChip").replace("{label}", label).replace("{count}", String(count));
          const selected = preset === id;
          return (
            <button
              key={id}
              type="button"
              aria-pressed={selected}
              className={`rounded-full border px-3 py-1 text-sm ${
                selected ? "border-primary bg-primary text-primary-foreground" : "border-border"
              }`}
              onClick={() => selectPreset(id)}
              data-testid={`period-${id}`}
            >
              {text}
            </button>
          );
        })}
      </div>
      {preset === "custom" && (
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="period-from">{t("history.periodFrom")}</Label>
            <input
              id="period-from"
              type="date"
              min="1900-01-01"
              max={clock}
              value={scope.periodFrom || ""}
              onChange={(event) => set({ periodFrom: event.target.value, excludedIds: [] })}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
              data-testid="period-from"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="period-to">{t("history.periodTo")}</Label>
            <input
              id="period-to"
              type="date"
              min="1900-01-01"
              max={clock}
              value={scope.periodTo || ""}
              onChange={(event) => set({ periodTo: event.target.value, excludedIds: [] })}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
              data-testid="period-to"
            />
          </div>
          {customPeriodError(scope) ? (
            <p className="text-sm text-destructive sm:col-span-2" data-testid="period-error">
              {t("history.periodInvalid")}
            </p>
          ) : null}
        </div>
      )}
      {undatedCount > 0 && (
        <Check
          testId="period-undated"
          checked={scope.includeUndated !== false}
          onChange={(value) => set({ includeUndated: value, excludedIds: [] })}
          label={t("history.periodUndated").replace("{count}", String(undatedCount))}
        />
      )}
      <Check
        testId="scope-profile"
        checked={scope.includeProfile}
        onChange={(value) => set({ includeProfile: value })}
        label={t("history.scopeProfile")}
      />
      <Check
        testId="scope-identity"
        checked={scope.includeIdentity}
        onChange={(value) => set({ includeIdentity: value })}
        label={t("history.scopeIdentity")}
      />
      <Check
        testId="scope-ai"
        checked={scope.includeAi}
        onChange={(value) => set({ includeAi: value })}
        label={t("history.includeAiOpinions")}
      />
      <p className="text-xs text-muted-foreground">{t("history.exportAiHint")}</p>
      <Check
        testId="scope-documents"
        checked={scope.attachDocuments}
        onChange={(value) => set({ attachDocuments: value })}
        label={t("history.scopeDocuments")}
      />
      <div className="space-y-1">
        <Label htmlFor="scope-note">{t("history.scopeNote")}</Label>
        <textarea
          id="scope-note"
          className="flex min-h-[72px] w-full rounded-md border border-input bg-card text-foreground placeholder:text-muted-foreground px-3 py-2 text-sm"
          maxLength={1000}
          value={scope.note}
          placeholder={t("history.scopeNotePlaceholder")}
          onChange={(event) => set({ note: event.target.value })}
          data-testid="scope-note"
        />
      </div>
      <button
        type="button"
        className="text-sm text-primary hover:underline"
        onClick={() => set({ choosing: !scope.choosing })}
        data-testid="scope-choose-records"
      >
        {t("history.exportChooseRecords")}
      </button>
      {showTruncation ? (
        <p className="text-xs text-muted-foreground" data-testid="scope-picker-truncated">
          {t("history.pickerTruncated").replace("{n}", String(loadedCount))}
        </p>
      ) : null}
      {scope.choosing && (
        <div
          className="max-h-40 overflow-y-auto border border-border rounded-md p-2 space-y-1"
          data-testid="scope-record-list"
        >
          {pickerRows(visible).map((row) => {
            if (row.type === "record") {
              return (
                <RecordChoice
                  key={row.item.record_id}
                  item={row.item}
                  excluded={excluded}
                  onToggle={toggleRecord}
                  t={t}
                />
              );
            }
            const ids = [
              ...(row.caseRecord ? [row.caseRecord.record_id] : []),
              ...row.files.map((file) => file.record_id),
            ];
            const included = ids.filter((id) => !excluded.includes(id)).length;
            const header = groupHeaderText(row.caseRecord, row.files, t);
            return (
              <div key={`group-${row.queryId}`} className="space-y-1">
                {row.caseRecord ? (
                  <GroupCheckbox
                    testId={`scope-group-${row.queryId}`}
                    checked={included === ids.length}
                    indeterminate={included > 0 && included < ids.length}
                    onChange={(checked) => toggleGroup(ids, checked)}
                    label={header}
                  />
                ) : (
                  <p
                    className="text-sm text-muted-foreground"
                    data-testid={`scope-group-label-${row.queryId}`}
                  >
                    {header}
                  </p>
                )}
                {row.files.map((file) => (
                  <RecordChoice
                    key={file.record_id}
                    item={file}
                    excluded={excluded}
                    onToggle={toggleRecord}
                    t={t}
                    indented
                  />
                ))}
              </div>
            );
          })}
        </div>
      )}
      <p className="text-sm" data-testid="scope-preview">
        {previewLine || t("history.scopePreviewLoading")}
      </p>
    </div>
  );
}
